import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from "recharts";
import { fetchFarm } from "@/lib/api";
import { fmt } from "@/lib/risk";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const KEYS = [
    { key: "rain_24h", label: "24h" },
    { key: "rain_yesterday", label: "Yesterday" },
    { key: "rain_7d", label: "7d" },
    { key: "rain_14d", label: "14d" },
    { key: "rain_30d", label: "30d" },
    { key: "rain_growing_season", label: "Growing" },
    { key: "rain_ytd", label: "Annual" },
];

export default function RainfallPage() {
    const { data } = useQuery({ queryKey: ["farm"], queryFn: fetchFarm, refetchInterval: 60_000 });
    const [range, setRange] = useState("rain_24h");

    if (!data) return <div className="h-40 bg-white rounded-2xl border animate-pulse" />;

    const chart = data.stations.map((s) => ({ name: s.name, value: s[range] }));
    const total = chart.reduce((a, r) => a + r.value, 0);
    const label = KEYS.find((k) => k.key === range).label;

    return (
        <div className="space-y-6" data-testid="rainfall-page">
            <div>
                <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Rainfall</h1>
                <p className="text-sm text-slate-500 mt-1">Compare rainfall totals across every station</p>
            </div>

            <Tabs value={range} onValueChange={setRange}>
                <TabsList className="bg-white border border-slate-200 p-1 flex-wrap h-auto">
                    {KEYS.map((k) => (
                        <TabsTrigger key={k.key} value={k.key} data-testid={`rain-range-${k.label}`}>{k.label}</TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 fw-card p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Rainfall by station · {label}</h2>
                        <div className="text-xs text-slate-500 fw-metric">Total: <span className="font-bold text-slate-900">{fmt(total, 1)} mm</span></div>
                    </div>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chart} layout="vertical" margin={{ left: 10, right: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 12 }} />
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 13, fontWeight: 600 }} width={80} />
                                <Tooltip contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12 }} />
                                <Bar dataKey="value" fill="#2563EB" radius={[0, 6, 6, 0]}>
                                    <LabelList dataKey="value" position="right" formatter={(v) => `${fmt(v, 1)} mm`} style={{ fill: "#0F172A", fontSize: 12, fontWeight: 700 }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="fw-card p-5">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>All ranges</h2>
                    <div className="space-y-2">
                        {data.stations.map((s) => (
                            <div key={s.id} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                                <div className="font-semibold text-slate-900">{s.name}</div>
                                <div className="grid grid-cols-2 gap-y-1 gap-x-3 mt-1 text-xs text-slate-600">
                                    {KEYS.map((k) => (
                                        <div key={k.key} className="flex justify-between">
                                            <span>{k.label}</span>
                                            <span className="fw-metric font-semibold text-slate-900">{fmt(s[k.key], 1)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Temperature overview */}
            <TempOverview stations={data.stations} />
        </div>
    );
}

function TempOverview({ stations }) {
    const chart = stations.map((s) => ({ name: s.name, max: s.max_temp, min: s.min_temp }));
    return (
        <div className="fw-card p-5">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Temperature Overview · Today</h2>
                <span className="text-xs text-slate-500">Max / Min per station</span>
            </div>
            <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12, fontWeight: 600 }} />
                        <YAxis tick={{ fontSize: 12 }} unit="°" />
                        <Tooltip contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12 }} />
                        <Bar dataKey="max" name="Max" fill="#DC2626" radius={[6, 6, 0, 0]} />
                        <Bar dataKey="min" name="Min" fill="#2563EB" radius={[6, 6, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
